import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FilePieChart, Download, Calendar, DollarSign, FileType } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ExportToExcelButton from "../components/ExportToExcelButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "bg-slate-500/10 text-slate-700 border-slate-500/30",
  calculated: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  approved: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  processed: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  paid: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
};

const fmtCurrency = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayrollSummaries() {
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [year, setYear] = useState("all");

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const url = year === "all"
        ? `${API}/payroll/periods`
        : `${API}/payroll/periods?year=${year}`;
      const res = await axios.get(url, { headers });
      setRuns(res.data || []);
    } catch {
      toast.error("Failed to load payroll runs");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Derived totals across visible runs
  const totals = runs.reduce(
    (acc, r) => {
      const gross = r.summary?.total_gross || r.totals?.gross || 0;
      const net = r.summary?.total_net || r.totals?.net || 0;
      const tax = r.summary?.total_tax || r.totals?.tax || 0;
      acc.gross += Number(gross || 0);
      acc.net += Number(net || 0);
      acc.tax += Number(tax || 0);
      acc.runs += 1;
      acc.employees += r.summary?.employee_count || (r.entries?.length || 0);
      return acc;
    },
    { gross: 0, net: 0, tax: 0, runs: 0, employees: 0 }
  );

  // Year filter options derived from data + current year
  const yearsSet = new Set();
  runs.forEach((r) => {
    const sd = r.period?.start_date;
    if (sd) yearsSet.add(sd.slice(0, 4));
  });
  yearsSet.add(String(new Date().getFullYear()));
  const yearOptions = ["all", ...Array.from(yearsSet).sort().reverse()];

  const exportCsv = () => {
    const rows = [
      ["Run", "Period Start", "Period End", "Pay Date", "Status", "Employees", "Gross", "Tax", "Net"],
      ...runs.map((r) => [
        r.id || "",
        r.period?.start_date || "",
        r.period?.end_date || "",
        r.period?.pay_date || "",
        r.status || "",
        r.summary?.employee_count || r.entries?.length || 0,
        r.summary?.total_gross || r.totals?.gross || 0,
        r.summary?.total_tax || r.totals?.tax || 0,
        r.summary?.total_net || r.totals?.net || 0,
      ]),
    ];
    const csv = rows
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_summaries_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center" data-testid="payroll-summaries-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="payroll-summaries-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <FilePieChart className="h-8 w-8 text-primary" />
            Payroll Summaries
          </h1>
          <p className="text-muted-foreground mt-1">
            Year-to-date overview of payroll runs &amp; totals
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid="year-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y} data-testid={`year-opt-${y}`}>
                  {y === "all" ? "All Years" : y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <ExportToExcelButton path="payroll" label="Excel" />
          <Button
            variant="outline"
            data-testid="export-ytd-pdf-btn"
            onClick={async () => {
              // Concatenate every visible run's PDF download (simplest UX: just download the most recent one as a smoke test).
              // For a true YTD PDF we'd build it server-side — out of scope for Phase 16.
              if (!runs.length) {
                toast.info("No payroll runs to export");
                return;
              }
              const latest = runs[0];
              const url = `${API}/payroll/periods/${latest.id}/export.pdf`;
              try {
                const res = await axios.get(url, { responseType: "blob", headers });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = `payroll_${latest.period?.start_date || "latest"}.pdf`;
                a.click();
                URL.revokeObjectURL(blobUrl);
              } catch {
                toast.error("Failed to download PDF");
              }
            }}
          >
            <FileType className="h-4 w-4 mr-2" />
            Export Latest PDF
          </Button>
        </div>
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card data-testid="kpi-runs">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Runs</CardDescription>
            <CardTitle className="text-3xl">{totals.runs}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-employees">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Employee Pays</CardDescription>
            <CardTitle className="text-3xl">{totals.employees}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-gross">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" /> Gross
            </CardDescription>
            <CardTitle className="text-2xl">{fmtCurrency(totals.gross)}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-tax">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Tax Withheld</CardDescription>
            <CardTitle className="text-2xl">{fmtCurrency(totals.tax)}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="kpi-net">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase">Net Paid</CardDescription>
            <CardTitle className="text-2xl">{fmtCurrency(totals.net)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Runs table */}
      <Card>
        <CardHeader>
          <CardTitle>Payroll Runs</CardTitle>
          <CardDescription>Sorted newest first</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FilePieChart className="h-12 w-12 mx-auto mb-2 opacity-40" />
              <p>No payroll runs yet for this period.</p>
              <p className="text-sm">Create a run from the Payroll module.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Pay Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((r) => {
                    const gross = r.summary?.total_gross ?? r.totals?.gross ?? 0;
                    const tax = r.summary?.total_tax ?? r.totals?.tax ?? 0;
                    const net = r.summary?.total_net ?? r.totals?.net ?? 0;
                    const empCount = r.summary?.employee_count ?? (r.entries?.length || 0);
                    return (
                      <TableRow key={r.id} data-testid={`run-${r.id}`}>
                        <TableCell className="font-medium">
                          {r.period?.start_date}
                          {r.period?.end_date ? ` → ${r.period.end_date}` : ""}
                        </TableCell>
                        <TableCell>{r.period?.pay_date || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>
                            {r.status || "draft"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{empCount}</TableCell>
                        <TableCell className="text-right">{fmtCurrency(gross)}</TableCell>
                        <TableCell className="text-right">{fmtCurrency(tax)}</TableCell>
                        <TableCell className="text-right font-medium">{fmtCurrency(net)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
